import addDays from 'date-fns/addDays'
import subDays from 'date-fns/subDays'
import { observer } from 'mobx-react'
import React, { FC } from 'react'
import styled from 'styled-components'

import {
  _M_Normal,
  formatDate,
  HorizontalAlignment,
  mainColors,
  parseISODate,
  SwitchButton,
  useIsTabletOrMobile,
} from '@avantstay/backoffice-core'

import { DatePickerBasedFilter } from '@new/__global/components/Filters/FiltersVariations/DatePicker'
import { FilterRenderingComponentProps } from '@new/__global/components/FiltersFramework'
import { filtersLabels } from '@new/domains/bookings/screens/bookingsList/utils/labels'
import { AvailableDatesRange } from '@new/domains/fastBookingsTapeChart/__types'
import { formatDateRange, parseDateRange } from '@new/domains/fastBookingsTapeChart/__utils'
import {
  ISO_DATE_FORMAT,
  TIME_FRAME_DAYS,
} from '@new/domains/fastBookingsTapeChart/config/contants'
import { fastBookingsTapeChartFiltersStore } from '@new/domains/fastBookingsTapeChart/internal'

const SwitchContainer = styled.div`
  ${_M_Normal};
  border-top: 1px solid ${mainColors.neutralMedium};
  padding-top: 14px;
  display: flex;
  gap: 8px;
  margin-top: 10px;
`

const _AvailableDatesFilter: FC<FilterRenderingComponentProps<AvailableDatesRange>> = ({
  activeValue,
  label,
  onChange,
  isAccordionOpen,
  isSidebarFilter,
}) => {
  const isTabletOrMobile = useIsTabletOrMobile()

  const getDateRangeObject = (value?: string): AvailableDatesRange | undefined => {
    const parsedRange = value ? parseDateRange(value) : undefined

    if (!parsedRange) {
      return undefined
    }

    if (parsedRange.checkIn === parsedRange.checkOut) {
      return {
        ...parsedRange,
        checkOut: formatDate(addDays(parseISODate(parsedRange.checkOut), 1)),
        ignoreBlocks: activeValue?.ignoreBlocks || false,
      }
    }

    return {
      ...parsedRange,
      ignoreBlocks: activeValue?.ignoreBlocks || false,
    }
  }

  const pushChange = (value?: AvailableDatesRange) => {
    onChange(value)

    // checking if we should change viewFrom (if selected range is outside of current tapechart rage
    if (value) {
      const { startDate, endDate, setViewFrom } = fastBookingsTapeChartFiltersStore
      const start = formatDate(startDate, ISO_DATE_FORMAT)
      const end = formatDate(endDate, ISO_DATE_FORMAT)
      const { checkIn, checkOut } = value
      if (!(start < checkIn && end > checkOut)) {
        setViewFrom(subDays(parseISODate(checkIn), TIME_FRAME_DAYS / 2))
      }
    }
  }

  const handleRangeChange = (value?: string) => {
    pushChange(getDateRangeObject(value))
  }
  const handleSwitchChange = () => {
    if (activeValue) {
      pushChange({
        ...activeValue,
        ignoreBlocks: !activeValue.ignoreBlocks,
      })
    }
  }

  return (
    <DatePickerBasedFilter
      title={filtersLabels.availableDates}
      initialValue={activeValue ? formatDateRange(activeValue) : undefined}
      placeholder="Any date"
      onChange={handleRangeChange}
      labelInternal={label}
      shouldAcceptEmptyDate
      clearButtonLabel={isTabletOrMobile ? 'Reset' : 'Clear'}
      horizontalAlignment={isTabletOrMobile ? HorizontalAlignment.Right : HorizontalAlignment.Left}
      inputWidthMaxContent
      isAccordionOpen={isAccordionOpen}
      isSidebarFilter={isSidebarFilter}
      children={
        <SwitchContainer>
          <SwitchButton
            id="availableDatesSwitch"
            onChange={handleSwitchChange}
            checked={activeValue?.ignoreBlocks}
          />
          <span>
            Show properties with <b>blocks</b> for selected period
          </span>
        </SwitchContainer>
      }
    />
  )
}

export const AvailableDatesFilter = observer(_AvailableDatesFilter)
